import { Fragment, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Upload, FileText, Tag, FileJson, Check, Unlock, Trash2, Camera, ShieldCheck, PackagePlus, PackageCheck } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { CameraScan } from '../components/CameraScan'
import { ChannelIcon } from '../components/ChannelIcon'
import { useActiveTenant } from '../contexts/tenant'
import { useAuth } from '../contexts/auth'

type BatchRow = {
  id: string; reference: string | null; status: string; channel: string | null; itemCount: number; orderCount: number
  hasReport: boolean; hasLabels: boolean
  releasedAt: string | null; abastecidoAt: string | null; impressoAt: string | null; baixadoAt: string | null
  createdAt: string
}
type BatchMov = {
  reference: string
  linhas: { sku: string; quantidade: number; baixado: number; abastecido: number; saldo: number }[]
  ledger: { sku: string | null; movementType: string; quantity: number; notes: string | null; movedAt: string }[]
}
type ConflitoRes = {
  total: number; mesma: number; diferente: number; semLeitura: number
  itens: { orderId: string; pedido: string; nf: string | null; carrier: string | null
           rastreioMe: string | null; veredito: 'mesma' | 'diferente' | 'sem-leitura'
           rastreiosNoAnexo: string[] }[]
}
type Alloc = { palletItemId: string; palletId: string; palletCode: string; lotNumber: string | null; expiryDate: string | null; boxes: number; aberto?: boolean
  sequencia?: number | null; motivo?: string; inconsistente?: boolean }
type PlanRow = { sku: string; productId: string | null; name: string | null; unitsPerBox: number | null; demanda: number; fracionamento: number; deficit: number; allocations: Alloc[]; warning: string | null }

function readAsDataURL(file: File): Promise<string> {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(file) })
}

export function SeparacaoArquivoPage() {
  const tenantId = useActiveTenant()
  const { user } = useAuth()
  const qc = useQueryClient()
  const isAdmin = user?.role === 'admin'
  const [novo, setNovo] = useState(false)
  const [expandId, setExpandId] = useState<string | null>(null)
  const [fStatus, setFStatus] = useState('')
  const [fDe, setFDe] = useState('')
  const [fAte, setFAte] = useState('')
  const refresh = () => qc.invalidateQueries({ queryKey: ['separations', tenantId] })

  const { data: all = [] } = useQuery<BatchRow[]>({
    queryKey: ['separations', tenantId],
    queryFn: async () => (await api.get('/separations', { params: { tenantId } })).data,
    enabled: !!tenantId,
    refetchInterval: 15000,
  })

  const batches = all.filter(b => {
    if (fStatus && b.status !== fStatus) return false
    const d = b.createdAt.slice(0, 10)
    if (fDe && d < fDe) return false
    if (fAte && d > fAte) return false
    return true
  })
  const temFiltro = !!(fStatus || fDe || fAte)

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Separação</h2>
          <p className="text-sm text-gray-500">Lotes (ondas) gerados por <b>arquivo</b> ou a partir de <b>pedidos</b> (na tela Pedidos). Abastecer → Imprimir → Concluir.</p>
        </div>
        {isAdmin && (
          <Button size="sm" variant={novo ? 'secondary' : 'primary'} onClick={() => setNovo(v => !v)}>
            <Upload className="h-4 w-4" /> {novo ? 'Fechar' : 'Novo lote por arquivo'}
          </Button>
        )}
      </div>

      {isAdmin && novo && <NovoLote tenantId={tenantId} onCreated={() => { refresh(); setNovo(false) }} />}

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">Status</label>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)}
            className="h-9 rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none">
            <option value="">Todos</option>
            <option value="draft">Rascunho</option>
            <option value="released">Liberado</option>
            <option value="done">Concluído</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">De</label>
          <input type="date" value={fDe} onChange={e => setFDe(e.target.value)}
            className="h-9 rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">Até</label>
          <input type="date" value={fAte} onChange={e => setFAte(e.target.value)}
            className="h-9 rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
        </div>
        {temFiltro && (
          <button onClick={() => { setFStatus(''); setFDe(''); setFAte('') }}
            className="h-9 text-xs text-gray-500 hover:text-gray-700">× limpar filtros</button>
        )}
        <span className="ml-auto self-center text-xs text-gray-400">{batches.length} de {all.length} lote(s)</span>
      </div>

      <Card>
        <CardContent className="p-0">
          {batches.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-400">
              {temFiltro ? 'Nenhum lote com esses filtros.' : (isAdmin ? 'Nenhum lote — crie por arquivo acima, ou selecione pedidos em Pedidos.' : 'Nenhum lote liberado ainda.')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Lote</th>
                    <th className="px-4 py-3 text-left font-medium">Canal</th>
                    <th className="px-4 py-3 text-center font-medium">Pedidos</th>
                    <th className="px-4 py-3 text-center font-medium" title="Produtos distintos (SKUs) do lote">SKUs</th>
                    <th className="px-4 py-3 text-center font-medium">Abastecer</th>
                    <th className="px-4 py-3 text-center font-medium">Imprimir</th>
                    <th className="px-4 py-3 text-center font-medium">Concluir</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    {isAdmin && <th className="px-4 py-3" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {batches.map(b => (
                    <BatchRow key={b.id} b={b} isAdmin={isAdmin}
                      expanded={expandId === b.id} onExpand={() => setExpandId(expandId === b.id ? null : b.id)}
                      onChange={refresh} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ── Linha da planilha (com detalhe de abastecimento expansível) ─────────────

const STATUS: Record<string, { label: string; variant: 'default' | 'info' | 'warning' | 'success' }> = {
  draft: { label: 'Rascunho', variant: 'default' },
  released: { label: 'Liberado', variant: 'info' },
  done: { label: 'Concluído', variant: 'success' },
  cancelled: { label: 'Cancelado', variant: 'warning' },
}

function BatchRow({ b, isAdmin, expanded, onExpand, onChange }: {
  b: BatchRow; isAdmin: boolean; expanded: boolean; onExpand: () => void; onChange: () => void
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [printed, setPrinted] = useState<Set<'report' | 'labels'>>(new Set())
  // Conferência do conflito de etiqueta (anexo x Melhor Envio) — sob demanda:
  // abre cada PDF, então não roda sozinha ao carregar a tela.
  const [conf, setConf] = useState<{ carregando: boolean; erro: string | null; res: ConflitoRes | null }>(
    { carregando: false, erro: null, res: null })

  // Tira da onda os pedidos cuja etiqueta não é confiável, sem estornar tudo:
  // eles voltam para a fila "a separar" e a onda segue com o resto.
  async function tirarDaOnda(ids: string[], descricao: string) {
    if (!ids.length) return
    if (!window.confirm(`Tirar ${ids.length} pedido(s) desta onda (${descricao})?\n\nEles voltam para a fila "a separar" e podem entrar numa onda própria. A lista de SKUs da onda é recalculada com quem ficou.`)) return
    setBusy('remover')
    try {
      const { data: r } = await api.post(`/separations/${b.id}/remover-pedidos`, { orderIds: ids })
      const presos = (r.presos ?? []) as { orderNumber: string; status: string }[]
      const extra = presos.length ? `\n${presos.length} não voltaram para a fila (já passaram da separação): ${presos.map((x) => x.orderNumber).join(', ')}` : ''
      const abast = r.avisoAbastecimento ? `\n${r.avisoAbastecimento}` : ''
      alert(`${r.removidos} pedido(s) saíram da onda. Ficaram ${r.restantes} pedido(s) e ${r.skus} SKU(s).${extra}${abast}`)
      setConf({ carregando: false, erro: null, res: null })
      onChange()
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      alert(err.response?.data?.title ?? 'Falha ao tirar os pedidos da onda')
    } finally { setBusy(null) }
  }

  async function conferirConflito() {
    setConf({ carregando: true, erro: null, res: null })
    try {
      const { data } = await api.get<ConflitoRes>(`/separations/${b.id}/conflito-etiquetas`)
      setConf({ carregando: false, erro: null, res: data })
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setConf({ carregando: false, erro: err.response?.data?.title ?? 'Falha ao conferir as etiquetas', res: null })
    }
  }
  const released = b.status === 'released' || b.status === 'done'
  const isArquivo = b.orderCount === 0 // lote por arquivo (sem pedidos vinculados)
  const st = STATUS[b.status] ?? STATUS.draft
  const cell = 'px-4 py-3 text-center'

  async function release() { setBusy('rel'); try { await api.post(`/separations/${b.id}/release`); onChange() } finally { setBusy(null) } }
  // Estornar a onda: os pedidos voltam para a fila "a separar" e podem entrar
  // numa onda nova. O que o cancelamento NÃO desfaz é o estoque — se a onda já
  // abasteceu ou já baixou, esses movimentos ficam, e o aviso diz isso antes,
  // porque depois de confirmado não há como voltar por aqui.
  async function cancel() {
    const feito = [b.abastecidoAt ? 'abastecimento' : null, b.baixadoAt ? 'baixa de estoque' : null].filter(Boolean)
    const alerta = feito.length
      ? `ATENÇÃO: esta onda já teve ${feito.join(' e ')}. Cancelar devolve os pedidos à fila, MAS NÃO desfaz esse movimento de estoque.\n\n`
      : ''
    if (!window.confirm(`${alerta}Estornar a onda ${b.reference ?? ''}?\n\nOs pedidos que ainda estão em separação voltam para "a separar" e podem entrar numa onda nova. Quem já foi embalado ou expedido não volta.`)) return
    setBusy('cancel')
    try {
      const { data: r } = await api.post(`/separations/${b.id}/cancel`)
      onChange()
      const presos = (r.mantidos ?? []) as { orderNumber: string; status: string }[]
      const detalhe = presos.length
        ? `\n\n${presos.length} pedido(s) NÃO voltaram (já passaram da separação): ${presos.slice(0, 10).map((m) => m.orderNumber).join(', ')}${presos.length > 10 ? '…' : ''}`
        : ''
      alert(`Onda estornada. ${r.devolvidos} de ${r.pedidos} pedido(s) voltaram para a fila.${detalhe}`)
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      alert(err.response?.data?.title ?? 'Falha ao estornar a onda')
    } finally { setBusy(null) }
  }

  // Baixa um PDF via link (âncora) — não sofre bloqueio de pop-up como o
  // window.open em sequência (o 2º arquivo era engolido pelo bloqueador).
  async function baixarPdf(which: 'report' | 'labels', filename: string) {
    const res = await api.get(`/separations/${b.id}/${which}`, { responseType: 'blob' })
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
    const a = document.createElement('a')
    a.href = url; a.download = filename
    document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 15000)
  }

  // Relatório de separação GERADO pelo WsFull (a partir dos pedidos da onda).
  async function baixarRelatorioAuto() {
    setBusy('impr')
    try {
      const res = await api.get(`/separations/${b.id}/report-auto`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url; a.download = `relatorio-${b.reference ?? b.id}.pdf`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 15000)
      // Onda vinda de pedidos não tem relatório/etiqueta armazenados: o "Relatório
      // WsFull" É a impressão → marca o passo pra esteira poder concluir.
      if (released && !b.impressoAt) {
        try { await api.post(`/separations/${b.id}/step`, { step: 'impresso' }); onChange() } catch { /* não bloqueia o download */ }
      }
    } catch (e: any) {
      // 422 = lote de arquivo (sem pedidos vinculados). Mensagem clara.
      const msg = e?.response?.status === 422
        ? 'Este lote foi criado por upload (sem pedidos). O relatório WsFull só sai de ondas criadas a partir de Pedidos.'
        : (e?.response?.data?.title ?? 'Falha ao gerar relatório')
      alert(msg)
    } finally { setBusy(null) }
  }

  // Esteira na ordem do relatório, em três formatos:
  //   'a4' = relatório + DANFEs ; 'etiquetas' = só as etiquetas (térmica 10x15) ;
  //   'pacote' = por pedido, etiqueta + DANFE (10x15) — alta vazão.
  const [progresso, setProgresso] = useState<string | null>(null)
  const [conferencia, setConferencia] = useState<{
    parte: string; total: number; etiquetas: number; danfes: number
    pulados: number; motivos: string; amostra: string
  } | null>(null)

  // Onda grande estoura o tempo do servidor (60s) e volta 504 — aconteceu com
  // 1170 pedidos. O arquivo passa a sair em blocos: a onda continua inteira,
  // muda só o tamanho de cada download.
  //
  // Bloco menor quando o arquivo leva ETIQUETA: cada uma custa duas chamadas ao
  // Melhor Envio, e 250 delas não cabem em 60s quando a API começa a pedir
  // calma. A DANFE é montada aqui dentro, sem rede, e aguenta blocos maiores.
  const tamanhoBloco = (parte: 'a4' | 'etiquetas' | 'pacote') => (parte === 'a4' ? 250 : 100)

  async function baixarEsteira(parte: 'a4' | 'etiquetas' | 'pacote') {
    setBusy('impr'); setConferencia(null)
    try {
      const nome = parte === 'pacote' ? 'etiqueta-danfe' : parte === 'etiquetas' ? 'etiquetas' : 'relatorio-nf'
      const acumulado = { total: 0, etiquetas: 0, danfes: 0, pulados: 0, motivos: new Map<string, number>(), amostra: [] as string[] }
      let bloco = 1
      let blocos = 1

      do {
        setProgresso(blocos > 1 ? `gerando parte ${bloco} de ${blocos}…` : null)
        const res = await api.get(`/separations/${b.id}/esteira`, {
          params: { parte, tamanho: tamanhoBloco(parte), bloco }, responseType: 'blob',
        })
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        const h = res.headers as Record<string, string>
        const num = (k: string) => Number(h[k] ?? h[k.toLowerCase()] ?? 0)
        const txt = (k: string) => decodeURIComponent(h[k] ?? h[k.toLowerCase()] ?? '')
        blocos = Math.max(1, num('X-Esteira-Blocos'))

        const a = document.createElement('a')
        a.href = url
        a.download = `${nome}-${b.reference ?? b.id}${blocos > 1 ? `-parte${bloco}de${blocos}` : ''}.pdf`
        document.body.appendChild(a); a.click(); a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 15000)

        acumulado.total += num('X-Esteira-Total')
        acumulado.etiquetas += num('X-Esteira-Etiqueta')
        acumulado.danfes += num('X-Esteira-Danfe')
        acumulado.pulados += num('X-Esteira-Skipped')
        // Cada bloco manda "250 sem etiqueta"; juntar os textos produzia
        // "250 sem etiqueta | 54 sem etiqueta", como se fossem coisas diferentes.
        // O motivo é o mesmo — o que soma é a contagem.
        for (const parte of txt('X-Esteira-Motivos').split(' | ').filter(Boolean)) {
          const m = parte.match(/^(\d+)\s+(.*)$/)
          if (!m) continue
          acumulado.motivos.set(m[2], (acumulado.motivos.get(m[2]) ?? 0) + Number(m[1]))
        }
        if (txt('X-Esteira-Amostra')) acumulado.amostra.push(txt('X-Esteira-Amostra'))
        bloco++
      } while (bloco <= blocos)

      setProgresso(null)
      setConferencia({
        parte, total: acumulado.total, etiquetas: acumulado.etiquetas, danfes: acumulado.danfes,
        pulados: acumulado.pulados,
        motivos: [...acumulado.motivos.entries()].sort((a, b) => b[1] - a[1])
          .map(([m, n]) => `${n} ${m}`).join(' | '),
        // A amostra de cada bloco traz até 10 pedidos; juntas viravam uma parede
        // de texto. Dez bastam para achar o padrão.
        amostra: acumulado.amostra.join(' | ').split(' | ').slice(0, 10).join(' | '),
      })

      if (released && !b.impressoAt) {
        try { await api.post(`/separations/${b.id}/step`, { step: 'impresso' }); onChange() } catch { /* não bloqueia */ }
      }
    } catch (e: any) {
      const st = e?.response?.status
      // Com responseType blob, o corpo do erro vem como Blob — sem ler o texto,
      // a mensagem do servidor se perde e a tela chutava uma explicação fixa.
      let doServidor = ''
      try {
        const d = e?.response?.data
        const raw = d instanceof Blob ? await d.text() : typeof d === 'string' ? d : JSON.stringify(d ?? '')
        doServidor = (JSON.parse(raw)?.title ?? JSON.parse(raw)?.detail ?? '') as string
      } catch { /* corpo não-JSON: segue com o genérico */ }

      const msg = doServidor
        || (st === 422
          ? 'Este lote foi criado por upload (sem pedidos). A esteira só sai de ondas criadas a partir de Pedidos.'
          : st === 504
          ? 'O servidor levou tempo demais para montar este bloco. Tente de novo — se repetir, me avise para reduzir o tamanho do bloco.'
          : 'Falha ao gerar a esteira')
      alert(msg)
    } finally { setBusy(null); setProgresso(null) }
  }

  // Imprime UM arquivo (relatório OU etiquetas). Marca 'impresso' quando todos
  // os arquivos existentes já foram baixados ao menos uma vez.
  async function imprimirUm(which: 'report' | 'labels') {
    setBusy('impr')
    try {
      const ref = b.reference ?? b.id
      await baixarPdf(which, `${which === 'report' ? 'relatorio' : 'etiquetas'}-${ref}.pdf`)
      const np = new Set(printed); np.add(which); setPrinted(np)
      const need: ('report' | 'labels')[] = []
      if (b.hasReport) need.push('report')
      if (b.hasLabels) need.push('labels')
      if (!b.impressoAt && need.length > 0 && need.every((w) => np.has(w))) {
        await api.post(`/separations/${b.id}/step`, { step: 'impresso' })
        onChange()
      }
    } catch (e: any) { alert(e?.response?.data?.title ?? 'Falha ao baixar arquivo') }
    finally { setBusy(null) }
  }

  async function concluir() {
    if (!window.confirm('Concluir: dar baixa no estoque dos itens do lote (e marcar os pedidos como enviados)?')) return
    setBusy('baixa')
    try {
      const { data: r } = await api.post(`/separations/${b.id}/baixa`)
      const ped = r.pedidosEnviados ? ` · ${r.pedidosEnviados} pedido(s) enviado(s)` : ''
      const nf = r.notas ? ` · ${r.notas.shipped} nota(s) marcada(s) enviada(s)${r.notas.already ? ` (${r.notas.already} já estavam)` : ''}${r.notas.notFoundNfs?.length ? ` · não achadas: ${r.notas.notFoundNfs.join(', ')}` : ''}` : ''
      // Pedido que já tinha saído do estoque numa onda anterior NÃO sai de novo —
      // a resposta diz quem foi pulado, para ninguém achar que o saldo "sumiu".
      const pul = Array.isArray(r.pulados) && r.pulados.length
        ? `\n\n${r.pulados.length} pedido(s) NÃO baixaram de novo (já tinham saído do estoque antes): ${r.pulados.slice(0, 15).map((p: { orderNumber: string }) => p.orderNumber).join(', ')}${r.pulados.length > 15 ? '…' : ''}`
        : ''
      const est = typeof r.baixados === 'number' ? ` · ${r.baixados} pedido(s)/SKU(s) baixado(s), ${r.unidades ?? 0} unidade(s)` : ''
      alert(`Baixa aplicada${est}${ped}${nf}.${pul}`)
      onChange()
    } catch (e: any) { alert(e?.response?.data?.title ?? 'Falha na baixa') }
    finally { setBusy(null) }
  }

  const StepIcon = () => <Check className="mx-auto h-5 w-5 text-green-600" />

  const [movOpen, setMovOpen] = useState(false)
  const { data: mov, isFetching: movLoading } = useQuery<BatchMov>({
    queryKey: ['batch-mov', b.id],
    queryFn: async () => (await api.get(`/separations/${b.id}/movimentacoes`)).data,
    enabled: movOpen,
  })

  const [labelOpen, setLabelOpen] = useState(false)
  type LabelStatus = { total: number; comEtiqueta: number; semEtiqueta: number; upload: boolean
    duplaFonte?: number
    jaExpedidos?: number
    itens: { orderId: string; pedido: string; nf: string | null; canal: string | null; fonte: string | null; ready: boolean; motivo: string
             temAnexo?: boolean; temMelhorEnvio?: boolean; duplaFonte?: boolean; rastreioMe?: string | null }[] }
  const { data: lbl } = useQuery<LabelStatus>({
    queryKey: ['batch-label-status', b.id],
    queryFn: async () => (await api.get(`/separations/${b.id}/label-status`)).data,
    enabled: labelOpen,
  })
  const tenantId = useActiveTenant()
  const qcEtiq = useQueryClient()
  const [buscandoEtiq, setBuscandoEtiq] = useState(false)
  const [etiqMsg, setEtiqMsg] = useState<string | null>(null)

  // Busca profunda no Melhor Envio pelas NFs sem etiqueta (notas antigas fora da janela).
  async function buscarEtiquetasAntigas() {
    const nfs = (lbl?.itens ?? []).filter(i => !i.ready && i.nf).map(i => i.nf as string)
    if (!nfs.length || buscandoEtiq) return
    setBuscandoEtiq(true); setEtiqMsg('procurando no Melhor Envio…')
    try {
      const { data: start } = await api.post('/integrations/nf-label-search', { tenantId, nfs })
      if (start?.started === false) { setEtiqMsg(start.reason ?? 'já há uma busca em andamento'); setBuscandoEtiq(false); return }
      for (;;) {
        await new Promise(r => setTimeout(r, 2500))
        const { data: st } = await api.get('/integrations/nf-label-search/status', { params: { tenantId } })
        if (st?.msg) setEtiqMsg(st.msg)
        if (!st?.running) {
          const r = st?.result as { found?: number; totalWanted?: number; naoAchadas?: string[] } | undefined
          setEtiqMsg(r
            ? `${r.found ?? 0}/${r.totalWanted ?? nfs.length} etiqueta(s) achada(s)${r.naoAchadas?.length ? ` · não achei no ME: ${r.naoAchadas.join(', ')}` : ''}`
            : (st?.error ?? 'concluído'))
          break
        }
      }
      qcEtiq.invalidateQueries({ queryKey: ['batch-label-status', b.id] })
    } catch (e: any) {
      setEtiqMsg(e?.response?.data?.title ?? 'falha na busca de etiquetas')
    } finally { setBuscandoEtiq(false) }
  }

  return (
    <Fragment>
      <tr className={b.status === 'done' ? 'bg-green-50' : 'hover:bg-gray-50'}>
        <td className="px-4 py-3">
          <span className="font-mono font-semibold text-gray-900">{b.reference || 'sem ref.'}</span>
          <p className="text-xs text-gray-400">{new Date(b.createdAt).toLocaleDateString('pt-BR')}</p>
          <button onClick={() => setMovOpen(v => !v)} className="mt-0.5 text-[11px] text-blue-600 hover:underline">
            {movOpen ? 'ocultar movimentações' : 'ver movimentações'}
          </button>
          <button onClick={() => setLabelOpen(v => !v)} className="ml-2 mt-0.5 text-[11px] text-amber-600 hover:underline">
            {labelOpen ? 'ocultar etiquetas' : 'diagnosticar etiquetas'}
          </button>
        </td>
        <td className="px-4 py-3">
          {b.channel
            ? <span className="inline-flex items-center gap-1.5 text-sm text-gray-700"><ChannelIcon name={b.channel} /> {b.channel}</span>
            : <span className="text-xs text-gray-400">{b.orderCount > 0 ? 'misto' : 'arquivo'}</span>}
        </td>
        {/* Pedidos do lote. Lote por ARQUIVO não tem pedidos vinculados: "—" em
            vez de 0, que leria como "nenhum pedido" num lote que tem carga. */}
        <td className={cell + ' font-semibold text-gray-800'}>
          {b.orderCount > 0 ? b.orderCount : <span className="font-normal text-gray-400">—</span>}
        </td>
        <td className={cell + ' text-gray-600'}>{b.itemCount}</td>

        {/* Abastecer */}
        <td className={cell}>
          {b.abastecidoAt ? <StepIcon />
            : released ? <Button size="sm" variant={expanded ? 'primary' : 'secondary'} onClick={onExpand}><PackagePlus className="h-4 w-4" /> Abastecer</Button>
            : <span className="text-gray-300">—</span>}
        </td>

        {/* Imprimir — mesma dupla de botões pros dois tipos de lote; cada um baixa a
            fonte certa: onda de PEDIDOS gera pelo sistema; lote por ARQUIVO baixa o
            PDF que foi enviado. */}
        <td className={cell}>
          {!released ? <span className="text-gray-300">—</span>
            : (isArquivo && !b.hasReport && !b.hasLabels) ? (
              // Lote por arquivo só com JSON (sem PDFs) — impressão não se aplica; concluir não exige.
              b.impressoAt ? <StepIcon /> : <span className="text-xs text-gray-400">sem impressão</span>
            ) : (
              <div className="mx-auto flex max-w-[240px] flex-wrap items-center justify-center gap-1.5 lg:max-w-none">
                {b.impressoAt && <StepIcon />}
                <button type="button" disabled={busy === 'impr' || (isArquivo && !b.hasReport)}
                  onClick={() => (isArquivo ? imprimirUm('report') : baixarRelatorioAuto())}
                  title={isArquivo ? 'Baixa o relatório de separação enviado com o lote' : 'Gera o relatório de separação a partir dos pedidos da onda (WsFull)'}
                  className="inline-flex items-center gap-1 rounded border border-rose-300 bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-40">
                  <FileText className="h-3.5 w-3.5" /> Relatório WsFull
                </button>
                <button type="button" disabled={busy === 'impr' || (isArquivo && !b.hasLabels)}
                  onClick={() => (isArquivo ? imprimirUm('labels') : baixarEsteira('pacote'))}
                  title={isArquivo ? 'Baixa as etiquetas enviadas com o lote' : 'Por pedido: etiqueta seguida da DANFE, ambas 10×15, na ordem do relatório (impressora térmica) — alta vazão'}
                  className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-40">
                  <Tag className="h-3.5 w-3.5" /> Etiqueta + DANFE 10×15
                </button>
              </div>
            )}

            {/* Conferência do arquivo recém-gerado — fica junto do botão, não
                dentro do painel de diagnóstico (que pode estar oculto). */}
            {progresso && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">{progresso}</div>
            )}
            {conferencia && (
                  <div className={`rounded-lg border px-3 py-2 text-xs ${
                    conferencia.pulados ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-green-200 bg-green-50 text-green-800'
                  }`}>
                    <strong>
                      Arquivo gerado: {conferencia.etiquetas > 0 && `${conferencia.etiquetas} etiqueta(s)`}
                  {conferencia.etiquetas > 0 && conferencia.danfes > 0 && ' · '}
                  {conferencia.danfes > 0 && `${conferencia.danfes} DANFE(s)`}
                      {' '}de {conferencia.total} pedido(s).
                    </strong>
                {conferencia.pulados > 0 ? (
                      <>
                        <br />{conferencia.pulados} ficaram de fora — {conferencia.motivos}.
                    {conferencia.amostra && <><br /><span className="text-[11px]">{conferencia.amostra}</span></>}
                      </>
                    ) : ' Nada ficou de fora.'}
                  </div>
            )}
        </td>

        {/* Concluir (baixa) */}
        <td className={cell}>
          {b.baixadoAt ? <StepIcon />
            : released ? <Button size="sm" loading={busy === 'baixa'} onClick={concluir}><PackageCheck className="h-4 w-4" /> Concluir</Button>
            : <span className="text-gray-300">—</span>}
        </td>

        <td className="px-4 py-3"><Badge variant={st.variant}>{st.label}</Badge></td>

        {isAdmin && (
          <td className="px-4 py-3">
            <div className="flex items-center justify-end gap-1">
              {b.status === 'draft' && (
                <Button size="sm" variant="secondary" loading={busy === 'rel'} onClick={release}><Unlock className="h-4 w-4" /> Liberar</Button>
              )}
              {b.status !== 'cancelled' && b.status !== 'done' && (
                <button
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:border-red-300 hover:text-red-600"
                  title="Estorna a onda: os pedidos voltam para a fila e podem entrar numa onda nova"
                  disabled={busy === 'cancel'} onClick={cancel}>
                  <Trash2 className="h-3.5 w-3.5" /> Estornar
                </button>
              )}
            </div>
          </td>
        )}
      </tr>

      {expanded && released && !b.abastecidoAt && (
        <tr className="bg-blue-50/30">
          <td colSpan={isAdmin ? 9 : 8} className="px-4 py-4">
            <AbastecerDetail batchId={b.id} onDone={() => { onExpand(); onChange() }} />
          </td>
        </tr>
      )}

      {movOpen && (
        <tr className="bg-gray-50/70">
          <td colSpan={isAdmin ? 9 : 8} className="px-4 py-4">
            {movLoading && !mov ? (
              <p className="text-xs text-gray-400">carregando movimentações…</p>
            ) : !mov ? (
              <p className="text-xs text-gray-400">sem dados</p>
            ) : (
              <div className="space-y-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-gray-500">
                      <tr className="border-b border-gray-200">
                        <th className="px-2 py-1 text-left">SKU</th>
                        <th className="px-2 py-1 text-right">Qtd no lote</th>
                        <th className="px-2 py-1 text-right">Abastecido (palete→geral)</th>
                        <th className="px-2 py-1 text-right">Baixado (saída)</th>
                        <th className="px-2 py-1 text-right">Saldo atual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mov.linhas.map((l) => (
                        <tr key={l.sku} className="border-b border-gray-100">
                          <td className="px-2 py-1 font-mono">{l.sku}</td>
                          <td className="px-2 py-1 text-right">{l.quantidade}</td>
                          <td className="px-2 py-1 text-right">{l.abastecido || <span className="text-gray-300">0</span>}</td>
                          <td className="px-2 py-1 text-right">{l.baixado ? `-${l.baixado}` : <span className="text-amber-600">0 ⚠️</span>}</td>
                          <td className={`px-2 py-1 text-right font-semibold ${l.saldo < 0 ? 'text-red-600' : 'text-gray-800'}`}>{l.saldo}{l.saldo < 0 ? ' ⚠️' : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-gray-500">
                  <b>Baixado 0</b> = a saída não foi lançada (lote não concluído). <b>Saldo negativo</b> = o abastecer (palete→geral) faltou.
                  Ondas antigas podem mostrar <b>abastecido 0</b> mesmo tendo movido palete (só passamos a marcar o abastecimento com a referência agora).
                </p>
                {mov.ledger.length > 0 && (
                  <details className="text-[11px] text-gray-500">
                    <summary className="cursor-pointer">Lançamentos brutos ({mov.ledger.length})</summary>
                    <ul className="mt-1 space-y-0.5">
                      {mov.ledger.map((e, i) => (
                        <li key={i} className="font-mono">
                          {new Date(e.movedAt).toLocaleString('pt-BR')} · {e.sku ?? '—'} · {e.movementType} · {e.quantity} · {e.notes}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </td>
        </tr>
      )}

      {labelOpen && (
        <tr className="bg-amber-50/40">
          <td colSpan={isAdmin ? 9 : 8} className="px-4 py-4">
            {!lbl ? (
              <p className="text-xs text-gray-400">carregando diagnóstico…</p>
            ) : lbl.upload ? (
              <p className="text-xs text-gray-400">Lote criado por upload (sem pedidos) — a etiqueta vem do arquivo enviado, sem diagnóstico por pedido.</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-gray-600">
                  <b className="text-emerald-700">{lbl.comEtiqueta}</b> com etiqueta · <b className="text-amber-700">{lbl.semEtiqueta}</b> sem etiqueta (de {lbl.total})
                </p>
                {lbl.itens.some(i => !i.ready && i.nf) && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="secondary" loading={buscandoEtiq} onClick={buscarEtiquetasAntigas}
                      title="Procura no Melhor Envio, página a página, as NFs antigas que ficaram fora da janela do sync — e casa a etiqueta.">
                      Buscar etiquetas antigas no ME ({lbl.itens.filter(i => !i.ready && i.nf).length})
                    </Button>
                    {etiqMsg && <span className="text-xs text-gray-500">{etiqMsg}</span>}
                  </div>
                )}
                {!!lbl.jaExpedidos && (
                  <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                    <strong>{lbl.jaExpedidos} pedido(s) desta onda já foram expedidos.</strong> Eles ficam de fora da
                    impressão — a nota não pode sair duas vezes. Se TODOS estiverem nessa situação, o pacote não gera.
                  </p>
                )}
                {!!lbl.duplaFonte && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <p>
                      <strong>{lbl.duplaFonte} pedido(s) têm etiqueta anexada E envio no Melhor Envio.</strong> A impressa
                      é a anexada — se forem etiquetas diferentes, o pacote sai com rastreio que não confere com o envio.
                    </p>
                    {/* O aviso sozinho acusa todo mundo. A conferência lê a etiqueta
                        anexada e procura nela o rastreio do ME: achou, é a mesma
                        etiqueta e não há problema; não achou, é conflito de verdade. */}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        className="rounded-lg border border-amber-300 bg-white px-2 py-1 font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                        disabled={conf.carregando} onClick={conferirConflito}>
                        {conf.carregando ? 'conferindo…' : 'Conferir quais são etiquetas diferentes'}
                      </button>
                      {isAdmin && (
                        <button
                          className="rounded-lg border border-red-300 bg-white px-2 py-1 font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                          disabled={busy === 'remover'}
                          onClick={() => tirarDaOnda(
                            lbl.itens.filter(i => i.duplaFonte).map(i => i.orderId),
                            'anexo + envio no Melhor Envio')}>
                          {busy === 'remover' ? 'tirando…' : `Tirar da onda os ${lbl.duplaFonte} com anexo + ME`}
                        </button>
                      )}
                      {conf.erro && <span className="text-red-600">{conf.erro}</span>}
                    </div>
                    {conf.res && (
                      <div className="mt-2 space-y-2">
                        <p>
                          <b>{conf.res.mesma}</b> são a MESMA etiqueta (o rastreio do ME está impresso no anexo) ·{' '}
                          <b className={conf.res.diferente ? 'text-red-700' : ''}>{conf.res.diferente}</b> são
                          DIFERENTES{conf.res.semLeitura ? ` · ${conf.res.semLeitura} não deu para ler (etiqueta em imagem)` : ''}.
                        </p>
                        {conf.res.diferente === 0 && conf.res.semLeitura === 0 && (
                          <p className="text-emerald-700">Nenhum conflito real — pode imprimir esta onda.</p>
                        )}
                        {conf.res.itens.filter(i => i.veredito !== 'mesma').length > 0 && (
                          <table className="w-full text-xs">
                            <thead className="text-amber-700">
                              <tr className="border-b border-amber-200">
                                <th className="px-2 py-1 text-left">Pedido</th>
                                <th className="px-2 py-1 text-left">NF</th>
                                <th className="px-2 py-1 text-left">Rastreio no Melhor Envio</th>
                                <th className="px-2 py-1 text-left">Rastreio impresso no anexo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {conf.res.itens.filter(i => i.veredito !== 'mesma').map(i => (
                                <tr key={i.orderId} className="border-b border-amber-100">
                                  <td className="px-2 py-1 font-mono">{i.pedido}</td>
                                  <td className="px-2 py-1">{i.nf ?? '—'}</td>
                                  <td className="px-2 py-1 font-mono">{i.rastreioMe ?? '—'}</td>
                                  <td className="px-2 py-1 font-mono">
                                    {i.veredito === 'sem-leitura'
                                      ? <span className="text-gray-500">não deu para ler</span>
                                      : (i.rastreiosNoAnexo.join(', ') || '—')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {isAdmin && conf.res.itens.some(i => i.veredito !== 'mesma') && (
                          <button
                            className="rounded-lg border border-red-300 bg-white px-2 py-1 font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                            disabled={busy === 'remover'}
                            onClick={() => tirarDaOnda(
                              conf.res!.itens.filter(i => i.veredito !== 'mesma').map(i => i.orderId),
                              'só os que a conferência não confirmou')}>
                            Tirar da onda só estes {conf.res.itens.filter(i => i.veredito !== 'mesma').length}
                          </button>
                        )}
                        <p className="text-amber-700">
                          Quem sai volta para a fila "a separar". Na tela Pedidos, o filtro
                          <b> Etiqueta: só conflito</b> junta todos eles numa onda própria.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-gray-500">
                      <tr className="border-b border-gray-200">
                        <th className="px-2 py-1 text-left">Pedido</th>
                        <th className="px-2 py-1 text-left">NF</th>
                        <th className="px-2 py-1 text-left">Canal</th>
                        <th className="px-2 py-1 text-left">Etiqueta</th>
                        <th className="px-2 py-1 text-left">Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...lbl.itens.filter(i => !i.ready), ...lbl.itens.filter(i => i.ready)].map((it, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="px-2 py-1 font-mono">{it.pedido}</td>
                          <td className="px-2 py-1">{it.nf ?? '—'}</td>
                          <td className="px-2 py-1">{it.canal ?? '—'}</td>
                          <td className="px-2 py-1">{it.ready
                            ? it.duplaFonte
                              // Tem anexo E envio no Melhor Envio. A impressa é a
                              // anexada; se forem etiquetas diferentes, o pacote sai
                              // com rastreio que não confere com o envio.
                              ? <span className="font-medium text-amber-700">✓ Anexo <span className="text-gray-400">(+ ME sem uso)</span></span>
                              : <span className="text-emerald-700">✓ {it.fonte}</span>
                            : <span className="font-medium text-amber-700">✗ sem etiqueta</span>}</td>
                          <td className="px-2 py-1 text-gray-500">
                            {it.ready
                              ? (it.duplaFonte ? `confira: o envio no ME tem rastreio ${it.rastreioMe ?? '—'}` : '')
                              : it.motivo}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </Fragment>
  )
}

// ── Detalhe do abastecimento (plano + conferência do QR + aplicar) ──────────

function AbastecerDetail({ batchId, onDone }: { batchId: string; onDone: () => void }) {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [plan, setPlan] = useState<PlanRow[] | null>(null)
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set())
  const [scanFor, setScanFor] = useState<{ palletId: string; palletCode: string } | null>(null)
  const [scanStatus, setScanStatus] = useState<{ text: string; tone: 'ok' | 'warn' | 'err' } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  const allocs = (plan ?? []).flatMap(r => r.productId && r.unitsPerBox
    ? r.allocations.filter(a => a.boxes > 0).map(a => ({ palletItemId: a.palletItemId, productId: r.productId as string, lotId: (a as any).lotId ?? null, boxes: a.boxes, unitsPerBox: r.unitsPerBox as number }))
    : [])
  const pendingPallets = new Set<string>()
  plan?.forEach(r => r.allocations.forEach(a => { if (a.boxes > 0) pendingPallets.add(a.palletId) }))
  const allConfirmed = isAdmin || [...pendingPallets].every(id => confirmed.has(id))

  async function gerarPlano() {
    setBusy('plan'); setMsg(null)
    try {
      const { data } = await api.post(`/separations/${batchId}/plan`)
      const rows = data as PlanRow[]
      setPlan(rows); setConfirmed(new Set())
      // Nada a buscar no armazém → marca abastecido na hora (sem exigir clique)
      const precisa = rows.some(r => r.productId && r.unitsPerBox && r.allocations.some(a => a.boxes > 0))
      if (!precisa) {
        await api.post(`/separations/${batchId}/abastecer`, { allocations: [] })
        onDone()
      }
    }
    catch (e: any) { setMsg(e?.response?.data?.title ?? 'Falha ao gerar plano') }
    finally { setBusy(null) }
  }

  function onScan(text: string) {
    if (!scanFor) return
    if (text.trim() === scanFor.palletId) {
      setConfirmed(s => new Set(s).add(scanFor.palletId))
      setScanStatus({ text: `✓ ${scanFor.palletCode} conferido`, tone: 'ok' })
      setTimeout(() => setScanFor(null), 700)
    } else setScanStatus({ text: `Palete errado! Esperado ${scanFor.palletCode}`, tone: 'err' })
  }

  async function aplicar() {
    if (allocs.length && !allConfirmed) { setMsg('Confira o QR de todos os paletes (📷) antes.'); return }
    setBusy('abast'); setMsg(null)
    try {
      const { data: r } = await api.post(`/separations/${batchId}/abastecer`, { allocations: allocs })
      setMsg(`Abastecido: ${r.caixas} cx / ${r.unidades} un.`); onDone()
    } catch (e: any) { setMsg(e?.response?.data?.title ?? 'Falha ao abastecer') }
    finally { setBusy(null) }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-800">Abastecimento — traga do armazém</span>
        {!plan
          ? <Button size="sm" variant="secondary" loading={busy === 'plan'} onClick={gerarPlano}>Gerar plano</Button>
          : <Button size="sm" loading={busy === 'abast'} disabled={!allConfirmed} onClick={aplicar}><Check className="h-4 w-4" /> Aplicar</Button>}
      </div>
      {msg && <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">{msg}</div>}
      {plan && (allocs.length === 0
        ? <p className="text-sm text-green-700">✓ Fracionamento já cobre — nada a buscar. Pode aplicar (marca abastecido) ou pular.</p>
        : (
          <div className="space-y-2">
            {plan.filter(r => r.allocations.some(a => a.boxes > 0)).map(r => (
              <div key={r.sku} className="text-sm">
                <p className="font-medium text-gray-800">{r.name ?? r.sku} <span className="text-xs text-gray-400">(déf. {r.deficit})</span></p>
                {/* A ordem da lista É a ordem de abertura. Numerar e dizer o porquê
                    de cada um: sem isso a sequência parecia aleatória e ninguém
                    sabia se dava para seguir na marra ou se era erro do sistema. */}
                {r.allocations.filter(a => a.boxes > 0).map((a, i) => (
                  <div key={a.palletItemId} className="mt-1 flex items-center gap-2 rounded-md bg-white px-2 py-1.5">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600" title="ordem de abertura">{i + 1}</span>
                    <span className="flex-1">
                      Traga <b>{a.boxes} cx</b> do palete <b className="font-mono">{a.palletCode}</b>
                      {a.sequencia != null ? <span className="text-gray-400"> (nº {a.sequencia})</span> : null}
                      {a.aberto ? <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">ABERTO</span> : null}
                      {a.inconsistente ? <span className="ml-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700" title="Caixas × unidades por caixa não fecha com as unidades registradas — confira o palete e a configuração do produto.">CONFERIR</span> : null}
                      {a.lotNumber ? ` · lote ${a.lotNumber}` : ''}{a.expiryDate ? ` · val ${a.expiryDate}` : ''}
                      {a.motivo ? <span className="ml-1 text-xs text-gray-400">· {a.motivo}</span> : null}
                    </span>
                    <button onClick={() => { setScanStatus(null); setScanFor({ palletId: a.palletId, palletCode: a.palletCode }) }}
                      className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${confirmed.has(a.palletId) ? 'border-green-300 text-green-700' : 'border-gray-300 text-gray-600'}`}>
                      {confirmed.has(a.palletId) ? <><ShieldCheck className="h-3.5 w-3.5" /> Conferido</> : <><Camera className="h-3.5 w-3.5" /> Conferir</>}
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}

      {scanFor && (
        <CameraScan hint={`Bipe o QR do palete ${scanFor.palletCode}`} status={scanStatus}
          onScan={onScan} onClose={() => setScanFor(null)} />
      )}
    </div>
  )
}

// ── Admin: novo lote por arquivo ────────────────────────────────────────────

function NovoLote({ tenantId, onCreated }: { tenantId: string; onCreated: () => void }) {
  const [reference, setReference] = useState('')
  const [items, setItems] = useState<{ sku: string; quantity: number }[]>([])
  const [nfsText, setNfsText] = useState('')
  const [reportName, setReportName] = useState<string | null>(null)
  const [reportB64, setReportB64] = useState<string | null>(null)
  const [labelsName, setLabelsName] = useState<string | null>(null)
  const [labelsB64, setLabelsB64] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  async function onJson(file: File) {
    try {
      const data = JSON.parse(await file.text())
      const its = (data.itens ?? data.items ?? [])
        .map((i: any) => ({ sku: String(i.sku).trim(), quantity: Number(i.quantidade ?? i.quantity) }))
        .filter((i: any) => i.sku && i.quantity > 0)
      setItems(its)
      if (data.separacao) setReference(String(data.separacao))
      // Tenta achar as NFs no JSON (chaves comuns) — o operador pode editar/colar depois.
      const arr = (v: any) => Array.isArray(v) ? v : []
      const nfs = [
        ...arr(data.nfs), ...arr(data.notas),
        ...arr(data.itens ?? data.items).map((i: any) => i.nf ?? i.nfe ?? i.nota),
        ...arr(data.pedidos).map((p: any) => p.nf ?? p.nfe ?? p.numero_nf ?? p.numeroNf),
      ].map((n: any) => String(n ?? '').trim()).filter(Boolean)
      const uniq = [...new Set(nfs)]
      if (uniq.length) setNfsText(uniq.join('\n'))
      setMsg(`${its.length} item(ns) lidos${uniq.length ? ` · ${uniq.length} NF(s)` : ''}.`)
    } catch { setMsg('JSON inválido.') }
  }

  async function uploadEmPedacos(id: string, which: 'report' | 'labels', b64: string) {
    const CH = 600 * 1024
    for (let i = 0; i < b64.length; i += CH) {
      await api.post(`/separations/${id}/append`, { which, chunk: b64.slice(i, i + CH), first: i === 0 })
    }
  }

  async function criar() {
    if (!items.length || busy) return
    setBusy(true); setMsg(null)
    try {
      const nfs = nfsText.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)
      const { data } = await api.post('/separations', { tenantId, reference, items, nfs })
      if (reportB64) { setMsg('Enviando relatório…'); await uploadEmPedacos(data.id, 'report', reportB64) }
      if (labelsB64) { setMsg('Enviando etiquetas…'); await uploadEmPedacos(data.id, 'labels', labelsB64) }
      onCreated()
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao criar')
    } finally { setBusy(false) }
  }

  const upl = (icon: React.ReactNode, label: string, name: string | null, accept: string, onFile: (f: File) => void) => (
    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
      {icon} <span className="truncate">{name ?? label}</span>
      <input type="file" accept={accept} className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = '' }} />
    </label>
  )

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <p className="text-sm font-semibold text-gray-800">Novo lote por arquivo</p>
        <input value={reference} onChange={e => setReference(e.target.value)} placeholder="Referência (ex.: SEP-20260713-01)"
          className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none" />
        <div className="grid gap-2 sm:grid-cols-3">
          {upl(<FileJson className="h-4 w-4 text-blue-600" />, 'JSON (itens) *', items.length ? `${items.length} itens` : null, '.json,application/json', onJson)}
          {upl(<FileText className="h-4 w-4 text-gray-500" />, 'Relatório (PDF)', reportName, '.pdf,application/pdf', async f => { setReportName(f.name); setReportB64(await readAsDataURL(f)) })}
          {upl(<Tag className="h-4 w-4 text-gray-500" />, 'Etiquetas (PDF)', labelsName, '.pdf,application/pdf', async f => { setLabelsName(f.name); setLabelsB64(await readAsDataURL(f)) })}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-700">NFs do lote (opcional)</label>
          <textarea value={nfsText} onChange={e => setNfsText(e.target.value)} rows={3}
            placeholder="85258, 85228…  (ou uma por linha). Na Baixa, os pedidos dessas notas são marcados como ENVIADOS no sistema."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm" />
          <p className="mt-1 text-[11px] text-gray-400">Preenchido automático se o JSON trouxer as notas. Na baixa, casa cada NF com o pedido e marca <b>enviado</b> (sem baixar estoque de novo — o lote já baixa por SKU).</p>
        </div>
        {msg && <p className="text-xs font-medium text-gray-600">{msg}</p>}
        <div className="flex justify-end">
          <Button loading={busy} disabled={!items.length} onClick={criar}><Upload className="h-4 w-4" /> Criar lote</Button>
        </div>
      </CardContent>
    </Card>
  )
}
